import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Download, FileSpreadsheet, Ticket, Users, Wrench, History, Loader2,
} from "lucide-react";

const ENTITIES = [
  { key: "complaints", label: "Complaints", icon: Ticket, path: "/export/complaints.csv", desc: "Every ticket with status, SLA flags, engineer and timestamps." },
  { key: "customers", label: "Customers", icon: Users, path: "/export/customers.csv", desc: "Full customer directory with contact and address details." },
  { key: "appliances", label: "Appliances", icon: Wrench, path: "/export/appliances.csv", desc: "Registered appliances with warranty and bill numbers." },
  { key: "activity", label: "Activity Log", icon: History, path: "/export/activity.csv", desc: "Complete audit trail of status changes, notes and notifications." },
];

export default function Export() {
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/export/summary").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const download = async (path, fallbackName, key) => {
    setBusy(key);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : fallbackName;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="p-5 sm:p-8 max-w-4xl fade-up">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground">Administration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Export Data</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-xl">
          Download an offline copy of every record. CSV files open in any spreadsheet app; the Excel
          workbook bundles all four datasets into one file.
        </p>
      </div>

      {/* Excel — everything */}
      <div className="border border-klein bg-klein/5 p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="text-klein shrink-0" size={28} />
          <div>
            <div className="font-heading font-bold text-lg">Full Excel workbook</div>
            <div className="text-sm text-muted-foreground">
              One .xlsx file with separate sheets for Complaints, Customers, Appliances and Activity Log.
            </div>
          </div>
        </div>
        <Button
          data-testid="export-all-xlsx-button"
          disabled={busy === "all"}
          onClick={() => download("/export/all.xlsx", "roso_export.xlsx", "all")}
          className="rounded-sm bg-klein hover:bg-klein/90 text-white font-semibold h-11 px-6 whitespace-nowrap"
        >
          {busy === "all" ? <Loader2 className="animate-spin mr-1" size={16} /> : <Download size={16} className="mr-1" />}
          Download all (Excel)
        </Button>
      </div>

      {/* Per-entity CSV */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border">
        {ENTITIES.map((e) => {
          const Icon = e.icon;
          return (
            <div key={e.key} className="bg-white p-5 flex flex-col" data-testid={`export-card-${e.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-heading font-bold">
                  <Icon size={18} className="text-klein" /> {e.label}
                </div>
                <span className="font-mono text-sm font-bold text-muted-foreground" data-testid={`count-${e.key}`}>
                  {counts[e.key] ?? "—"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground flex-1">{e.desc}</p>
              <Button
                variant="outline"
                data-testid={`export-${e.key}-csv-button`}
                disabled={busy === e.key}
                onClick={() => download(e.path, `${e.key}.csv`, e.key)}
                className="rounded-sm mt-4 self-start"
              >
                {busy === e.key ? <Loader2 className="animate-spin mr-1" size={15} /> : <Download size={15} className="mr-1" />}
                Download CSV
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
